// Batch Reminder Dialog for Deliverables
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Bell, User, Loader2, CheckCircle2, Circle, Clock, AlertCircle, PauseCircle } from 'lucide-react';
import api from '../../services/api';

const statusIcons = {
  not_started: Circle,
  in_progress: Clock,
  completed: CheckCircle2,
  on_hold: PauseCircle,
};

const statusColors = {
  not_started: 'text-gray-400',
  in_progress: 'text-blue-500',
  completed: 'text-green-500',
  on_hold: 'text-yellow-500',
};

const BatchReminderDialog = ({ open, onClose, saleOpportunityId }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [incompleteDeliverables, setIncompleteDeliverables] = useState({ horse: [], owner: [] });
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(false);

  useEffect(() => {
    if (open && saleOpportunityId) {
      fetchIncompleteDeliverables();
    }
  }, [open, saleOpportunityId]);

  const fetchIncompleteDeliverables = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/deliverable-reminders/incomplete?sale_opportunity_id=${saleOpportunityId}`);

      // Only show deliverables that are incomplete AND have a responsible party assigned
      const horse = (response.data.horse_deliverables || []).filter(d => d.responsible_party);
      const owner = (response.data.owner_deliverables || []).filter(d => d.responsible_party);

      setIncompleteDeliverables({ horse, owner });

      // Auto-select all by default
      setSelectedIds([...horse.map(d => d.id), ...owner.map(d => d.id)]);
    } catch (error) {
      console.error('Error fetching incomplete deliverables:', error);
      toast.error('Failed to load incomplete deliverables');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one deliverable');
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/deliverable-reminders/send', {
        deliverable_ids: selectedIds,
        message: message || undefined,
        send_email: sendEmail,
        send_in_app: true
      });

      const { success_count, failed_count, skipped_count } = response.data;
      
      if (success_count > 0) {
        toast.success(`Sent ${success_count} reminder${success_count > 1 ? 's' : ''} successfully`);
      }
      if (skipped_count > 0) {
        toast.info(`Skipped ${skipped_count} deliverable${skipped_count > 1 ? 's' : ''} (already completed or no responsible party)`);
      }
      if (failed_count > 0) {
        toast.error(`Failed to send ${failed_count} reminder${failed_count > 1 ? 's' : ''}`);
      }

      handleClose();
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSelectedIds([]);
    setMessage('');
    setSendEmail(false);
    onClose();
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const allIds = [
      ...incompleteDeliverables.horse.map(d => d.id),
      ...incompleteDeliverables.owner.map(d => d.id)
    ];
    
    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const totalIncomplete = incompleteDeliverables.horse.length + incompleteDeliverables.owner.length;
  const allSelected = selectedIds.length === totalIncomplete && totalIncomplete > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Send Batch Reminders
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading incomplete deliverables...</p>
          </div>
        ) : totalIncomplete === 0 ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
            <p className="text-muted-foreground">All deliverables are complete! 🎉</p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-900">
                <strong>{totalIncomplete} incomplete deliverable{totalIncomplete > 1 ? 's' : ''} found with a responsible party assigned.</strong> Select which ones to send reminders for.
              </p>
            </div>

            {/* Select All */}
            <div className="flex items-center gap-2 pb-2 border-b">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                id="select-all"
              />
              <Label htmlFor="select-all" className="cursor-pointer font-medium">
                Select All ({selectedIds.length}/{totalIncomplete})
              </Label>
            </div>

            {/* Horse Deliverables */}
            {incompleteDeliverables.horse.length > 0 && (
              <div>
                <h3 className="font-medium text-sm mb-2">Horse Deliverables ({incompleteDeliverables.horse.length})</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {incompleteDeliverables.horse.map(deliverable => {
                    const StatusIcon = statusIcons[deliverable.status] || Circle;
                    const statusColor = statusColors[deliverable.status] || 'text-gray-400';
                    const isSelected = selectedIds.includes(deliverable.id);

                    return (
                      <div
                        key={deliverable.id}
                        className={`flex items-center gap-3 p-2 rounded border ${
                          isSelected ? 'bg-blue-50 border-blue-200' : 'bg-muted/20 border-border'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelection(deliverable.id)}
                          id={`deliverable-${deliverable.id}`}
                        />
                        <StatusIcon className={`w-4 h-4 ${statusColor}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {deliverable.deliverable_type_horse?.replace(/_/g, ' ')?.toUpperCase()}
                          </p>
                          {deliverable.nickname && (
                            <p className="text-xs text-muted-foreground">{deliverable.nickname}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {deliverable.status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Owner Deliverables */}
            {incompleteDeliverables.owner.length > 0 && (
              <div>
                <h3 className="font-medium text-sm mb-2">Owner Deliverables ({incompleteDeliverables.owner.length})</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {incompleteDeliverables.owner.map(deliverable => {
                    const StatusIcon = statusIcons[deliverable.status] || Circle;
                    const statusColor = statusColors[deliverable.status] || 'text-gray-400';
                    const isSelected = selectedIds.includes(deliverable.id);

                    return (
                      <div
                        key={deliverable.id}
                        className={`flex items-center gap-3 p-2 rounded border ${
                          isSelected ? 'bg-blue-50 border-blue-200' : 'bg-muted/20 border-border'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelection(deliverable.id)}
                          id={`deliverable-${deliverable.id}`}
                        />
                        <StatusIcon className={`w-4 h-4 ${statusColor}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {deliverable.deliverable_type?.replace(/_/g, ' ')?.toUpperCase()}
                          </p>
                          {deliverable.property_line && (
                            <p className="text-xs text-muted-foreground">Property Line: {deliverable.property_line}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {deliverable.status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Message */}
            <div className="space-y-2">
              <Label>Custom Message (Optional)</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a custom message to the reminder..."
                rows={3}
              />
            </div>

            {/* Email Option */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={sendEmail}
                onCheckedChange={setSendEmail}
                id="send-email"
              />
              <Label htmlFor="send-email" className="cursor-pointer text-sm">
                Also send email notifications
              </Label>
            </div>

            {selectedIds.length === 0 && (
              <p className="text-sm text-amber-600">
                ⚠️ No deliverables selected. Please select at least one.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendReminders}
            disabled={sending || loading || selectedIds.length === 0}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Bell className="w-4 h-4 mr-2" />
                Send {selectedIds.length} Reminder{selectedIds.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BatchReminderDialog;
